import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { getAudit, downloadAuditPdf, uploadSupplemental, getSupplementalDocs, chargeReport, getPaymentStatus } from '../services/api';

const severityConfig = {
  critical: { bg: 'bg-red-50', border: 'border-red-200', badge: 'bg-red-100 text-red-800', icon: '!', iconBg: 'bg-red-500' },
  warning: { bg: 'bg-yellow-50', border: 'border-yellow-200', badge: 'bg-yellow-100 text-yellow-800', icon: '!', iconBg: 'bg-yellow-500' },
  informational: { bg: 'bg-teal-50', border: 'border-teal-200', badge: 'bg-teal-100 text-teal-800', icon: 'i', iconBg: 'bg-teal-500' },
};

function isRequestTimeout(err) {
  return err?.code === 'ECONNABORTED' || /timeout/i.test(err?.message || '');
}

export default function AuditDetail() {
  const { id } = useParams();
  const location = useLocation();
  const seeded = location.state?.pendingAudit;
  const [audit, setAudit] = useState(
    seeded?.id === id ? { id, status: 'processing', ...seeded } : null
  );
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(!(seeded?.id === id));
  const [loadError, setLoadError] = useState('');
  const fetchInFlight = useRef(false);
  const [expandedFindings, setExpandedFindings] = useState({});
  const [filterSeverity, setFilterSeverity] = useState('all');

  // Supplemental upload state
  const [suppFiles, setSuppFiles] = useState([]);
  const [suppUploading, setSuppUploading] = useState(false);
  const [suppError, setSuppError] = useState('');
  const [suppDocs, setSuppDocs] = useState([]);
  const [showSupplemental, setShowSupplemental] = useState(false);

  const fetchAudit = useCallback(async () => {
    if (!id || fetchInFlight.current) return;
    fetchInFlight.current = true;
    try {
      const res = await getAudit(id);
      setAudit(res.data.audit);
      setFindings(res.data.findings || []);
      setLoadError('');
    } catch (err) {
      console.error('Failed to fetch audit:', err);
      if (isRequestTimeout(err)) {
        setAudit((prev) => prev || { id, status: 'processing' });
        setLoadError('');
      } else {
        setLoadError(err.response?.data?.error || 'Failed to load audit');
      }
    } finally {
      fetchInFlight.current = false;
      setLoading(false);
    }
  }, [id]);

  const fetchSuppDocs = useCallback(async () => {
    try {
      const res = await getSupplementalDocs(id);
      setSuppDocs(res.data.supplementalDocuments || []);
    } catch (err) {
      console.error('Failed to fetch supplemental docs:', err);
    }
  }, [id]);

  useEffect(() => {
    fetchAudit();
  }, [fetchAudit]);

  useEffect(() => {
    if (!audit || audit.status === 'processing') return;
    fetchSuppDocs();
  }, [id, audit?.status, fetchSuppDocs]);

  // Poll while processing (skip if a request is already in flight)
  useEffect(() => {
    if (audit?.status !== 'processing') return;
    const interval = setInterval(fetchAudit, 10000);
    return () => clearInterval(interval);
  }, [audit?.status, fetchAudit]);

  const toggleFinding = (findingId) => {
    setExpandedFindings(prev => ({ ...prev, [findingId]: !prev[findingId] }));
  };

  const [paymentStatus, setPaymentStatus] = useState(null);
  const [charging, setCharging] = useState(false);

  useEffect(() => {
    if (!audit || audit.status === 'processing') return;
    getPaymentStatus().then(res => setPaymentStatus(res.data)).catch(() => {});
  }, [id, audit?.status]);

  const handleDownloadPdf = async () => {
    try {
      // Check if payment is required and not yet paid
      const isPaid = paymentStatus?.paidAuditIds?.includes(id);
      if (paymentStatus?.hasCard && !isPaid) {
        setCharging(true);
        try {
          const chargeRes = await chargeReport(id);
          if (!chargeRes.data.success) {
            alert('Payment failed. Please update your card.');
            setCharging(false);
            return;
          }
          // Refresh payment status
          const updatedStatus = await getPaymentStatus();
          setPaymentStatus(updatedStatus.data);
        } catch (chargeErr) {
          alert(chargeErr.response?.data?.error || 'Payment failed. Please try again.');
          setCharging(false);
          return;
        }
        setCharging(false);
      }

      const res = await downloadAuditPdf(id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `ChartAudit-Report-${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert('Failed to download PDF');
    }
  };

  // Supplemental file drop
  const onSuppDrop = useCallback((acceptedFiles) => {
    setSuppFiles(prev => [...prev, ...acceptedFiles]);
    setSuppError('');
  }, []);

  const { getRootProps: getSuppRootProps, getInputProps: getSuppInputProps, isDragActive: isSuppDragActive } = useDropzone({
    onDrop: onSuppDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/csv': ['.csv'],
    },
    maxSize: 50 * 1024 * 1024,
  });

  const removeSuppFile = (index) => {
    setSuppFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSupplementalUpload = async () => {
    if (suppFiles.length === 0) return setSuppError('Please add at least one document');
    setSuppError('');
    setSuppUploading(true);

    try {
      const formData = new FormData();
      suppFiles.forEach(file => formData.append('documents', file));

      await uploadSupplemental(id, formData);

      // Clear files, refresh audit (it'll be in processing state)
      setSuppFiles([]);
      setShowSupplemental(false);
      fetchAudit();
      fetchSuppDocs();
    } catch (err) {
      setSuppError(err.response?.data?.error || 'Failed to upload supplemental documents');
    } finally {
      setSuppUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-teal-100 mb-3 animate-pulse">
          <svg className="w-6 h-6 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        </div>
        <p className="text-teal-700 font-semibold">Loading audit…</p>
        <p className="text-sm text-teal-400 mt-2">Checking audit status…</p>
      </div>
    );
  }
  if (!audit) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <p className="text-red-600">{loadError || 'Audit not found'}</p>
        <Link to="/audits" className="text-teal-600 hover:underline text-sm font-medium mt-4 inline-block">Back to Audits</Link>
      </div>
    );
  }

  const filteredFindings = filterSeverity === 'all' ? findings : findings.filter(f => f.severity === filterSeverity);
  const scoreColor = audit.compliance_score >= 80 ? 'text-green-600' : audit.compliance_score >= 60 ? 'text-yellow-600' : 'text-red-600';
  const hasCriticalOrWarning = findings.some(f => f.severity === 'critical' || f.severity === 'warning');

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <Link to="/audits" className="text-teal-600 hover:text-teal-700 hover:underline text-sm font-medium">&larr; Back to Audits</Link>
          <div className="flex items-center gap-3 mt-2">
            <h1 className="text-2xl font-bold text-gray-800">Audit Report</h1>
            {audit.service_type && (
              <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                audit.service_type === 'hospice'
                  ? 'bg-purple-100 text-purple-700'
                  : 'bg-teal-100 text-teal-700'
              }`}>
                {audit.service_type === 'hospice' ? 'Hospice' : 'Home Health'}
              </span>
            )}
            {audit.document_type && (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                {audit.document_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </span>
            )}
            {audit.reaudit_count > 0 && (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">
                Re-audited {audit.reaudit_count}x
              </span>
            )}
          </div>
          <p className="text-gray-500">{audit.original_filename} — {new Date(audit.created_at).toLocaleDateString()}</p>
          {audit.patient_full_name && <p className="text-gray-700 font-medium">Patient: {audit.patient_full_name}</p>}
          {audit.client_agency_name && <p className="text-gray-500">Agency: {audit.client_agency_name}</p>}
          {audit.patient_reference && <p className="text-gray-400 text-sm">Chart Ref: {audit.patient_reference}</p>}
        </div>
        <div className="flex flex-col gap-2 items-end">
          {audit.status === 'completed' && (
            <>
              <button onClick={handleDownloadPdf} disabled={charging}
                className="bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white px-4 py-2 rounded-xl font-medium text-sm shadow-md disabled:opacity-50">
                {charging ? 'Processing...' :
                  paymentStatus?.paidAuditIds?.includes(id) ? 'Download PDF Report' :
                  paymentStatus?.subscription?.isActive ?
                    (paymentStatus.subscription.reportsRemaining > 0
                      ? `Download Report (${paymentStatus.subscription.reportsRemaining} left this month)`
                      : `Download Report ($${paymentStatus.subscription.overagePrice || 40} overage)`)
                    : `Download PDF Report ($${paymentStatus?.reportPrice || 50})`}
              </button>
              {paymentStatus?.paidAuditIds?.includes(id) && (
                <span className="text-xs text-green-600 font-medium">Paid</span>
              )}
              {paymentStatus?.subscription?.isActive && !paymentStatus?.paidAuditIds?.includes(id) && (
                <span className="text-xs text-teal-600">Monthly Pro — {paymentStatus.subscription.reportsUsed}/{paymentStatus.subscription.reportsIncluded} used</span>
              )}
            </>
          )}
        </div>
      </div>

      {/* Processing state */}
      {audit.status === 'processing' && (
        <div className="glass-card border border-teal-200 rounded-2xl p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-teal-100 mb-4 animate-pulse">
            <svg className="w-8 h-8 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </div>
          <p className="text-xl font-semibold text-teal-800">
            {audit.reaudit_count > 0 ? 'Re-Audit in Progress' : 'AI Audit in Progress'}
          </p>
          <p className="text-teal-600 mt-2">
            {audit.reaudit_count > 0
              ? 'ChartAuditer is re-analyzing your documents with the supplemental files...'
              : 'ChartAuditer is analyzing your document against CMS standards...'}
          </p>
          <p className="text-sm text-teal-400 mt-4">Large charts often take 8–12 minutes. This page retries automatically.</p>
        </div>
      )}

      {audit.status === 'failed' && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-8 text-center">
          <p className="text-xl font-semibold text-red-800">Audit Failed</p>
          <p className="text-red-600 mt-2">{audit.error_message || 'An unexpected error occurred'}</p>
          <div className="mt-4 bg-white/70 rounded-xl p-4 inline-block text-left">
            <p className="text-sm font-semibold text-gray-700 mb-1">Failed file:</p>
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="text-sm text-gray-800 font-medium">{audit.original_filename}</span>
            </div>
            <p className="text-xs text-gray-500 mt-2">Please re-upload this file from the Upload & Audit page, or upload a corrected version below.</p>
          </div>
          {/* Allow supplemental upload even on failed audits */}
          <div className="mt-6 text-left">
            <div {...getSuppRootProps()}
              className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition
                ${isSuppDragActive ? 'border-orange-500 bg-orange-50' : 'border-gray-300 hover:border-orange-400 hover:bg-orange-50/30'}`}>
              <input {...getSuppInputProps()} />
              <svg className="w-8 h-8 mx-auto text-orange-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <p className="font-medium text-gray-700 text-sm">Drop corrected documents here to re-try the audit</p>
            </div>
            {suppFiles.length > 0 && (
              <div className="mt-3 space-y-2">
                {suppFiles.map((file, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-white border border-gray-200 rounded-xl px-3 py-2">
                    <span className="text-sm text-gray-700">{file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                    <button onClick={() => removeSuppFile(idx)} className="text-red-400 hover:text-red-600 text-xs font-medium">Remove</button>
                  </div>
                ))}
                <button onClick={handleSupplementalUpload} disabled={suppUploading}
                  className="mt-2 w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold py-2.5 rounded-xl shadow-md disabled:opacity-50">
                  {suppUploading ? 'Uploading & Re-Auditing...' : `Upload & Re-Audit (${suppFiles.length} file${suppFiles.length !== 1 ? 's' : ''})`}
                </button>
              </div>
            )}
            {suppError && <p className="text-red-600 text-sm mt-2">{suppError}</p>}
          </div>
        </div>
      )}

      {audit.status === 'completed' && (
        <>
          {/* Clinicians Section */}
          {audit.clinicians_data && (() => {
            let clinicians = [];
            try { clinicians = JSON.parse(audit.clinicians_data); } catch(e) {}
            if (clinicians.length === 0) return null;
            const groups = {};
            clinicians.forEach(c => {
              const d = c.discipline || 'Other';
              if (!groups[d]) groups[d] = [];
              groups[d].push(c);
            });
            return (
              <div className="mb-6 glass-card rounded-2xl shadow-sm border border-teal-100/50 p-5">
                <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3">Clinical Team Identified in Chart</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {Object.entries(groups).map(([discipline, members]) => (
                    <div key={discipline} className="flex items-start space-x-2">
                      <span className="text-sm font-bold text-teal-700 whitespace-nowrap">{discipline}:</span>
                      <span className="text-sm text-gray-700">
                        {members.map(m => `${m.name}${m.role ? ` (${m.role})` : ''}`).join(', ')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* PDGM LUPA Card (Home Health only) */}
          {audit.service_type !== 'hospice' && audit.pdgm_lupa_data && (() => {
            let pdgm = {};
            try { pdgm = JSON.parse(audit.pdgm_lupa_data); } catch(e) {}
            if (!pdgm.clinicalGrouping) return null;
            const visits = pdgm.documentedVisits || {};
            const met = pdgm.lupaThresholdMet;
            const threshold = pdgm.estimatedLupaThreshold;
            return (
              <div className={`mb-6 glass-card rounded-2xl shadow-sm border p-5 ${
                met === true ? 'border-green-200' : met === false ? 'border-red-200' : 'border-yellow-200'
              }`}>
                <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3">PDGM LUPA Analysis</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                  <div>
                    <p className="text-xs text-gray-500">Clinical Grouping</p>
                    <p className="text-sm font-semibold text-gray-800">{pdgm.clinicalGrouping}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Primary Dx</p>
                    <p className="text-sm font-semibold text-gray-800">{pdgm.primaryDiagnosis || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">LUPA Threshold</p>
                    <p className="text-sm font-semibold text-gray-800">{threshold || '?'} visits</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Total Documented Visits</p>
                    <p className="text-sm font-semibold text-gray-800">{visits.total || 0}</p>
                  </div>
                </div>
                {/* Visit breakdown */}
                <div className="flex flex-wrap gap-3 mb-3 text-xs">
                  {visits.SN != null && <span className="bg-teal-50 text-teal-700 px-2 py-1 rounded">SN: {visits.SN}</span>}
                  {visits.PT != null && <span className="bg-teal-50 text-teal-700 px-2 py-1 rounded">PT: {visits.PT}</span>}
                  {visits.OT != null && <span className="bg-teal-50 text-teal-700 px-2 py-1 rounded">OT: {visits.OT}</span>}
                  {visits.SLP != null && <span className="bg-teal-50 text-teal-700 px-2 py-1 rounded">SLP: {visits.SLP}</span>}
                  {visits.MSW != null && <span className="bg-teal-50 text-teal-700 px-2 py-1 rounded">MSW: {visits.MSW}</span>}
                  {visits.HHA != null && <span className="bg-teal-50 text-teal-700 px-2 py-1 rounded">HHA: {visits.HHA}</span>}
                </div>
                {/* LUPA verdict */}
                <div className={`rounded-xl px-4 py-2 text-center font-bold text-sm ${
                  met === true ? 'bg-green-100 text-green-800' : met === false ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                }`}>
                  LUPA Threshold: {met === true ? 'MET' : met === false ? 'NOT MET — Risk of LUPA reduction' : 'INDETERMINATE'}
                </div>
                {pdgm.notes && <p className="text-xs text-gray-500 mt-2">{pdgm.notes}</p>}
              </div>
            );
          })()}

          {/* HHCAPS Quality Card (Home Health only) */}
          {audit.service_type !== 'hospice' && audit.hhcaps_data && (() => {
            let hhcaps = {};
            try { hhcaps = JSON.parse(audit.hhcaps_data); } catch(e) {}
            const domains = [
              { key: 'careOfPatients', label: 'Care of Patients' },
              { key: 'communicationWithPatients', label: 'Communication with Patients' },
              { key: 'specificCareIssues', label: 'Specific Care Issues' },
              { key: 'overallRating', label: 'Overall Rating' },
              { key: 'willingnessToRecommend', label: 'Willingness to Recommend' },
            ];
            const hasDomains = domains.some(d => hhcaps[d.key]);
            if (!hasDomains) return null;
            return (
              <div className="mb-6 glass-card rounded-2xl shadow-sm border border-teal-100/50 p-5">
                <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3">HHCAPS Quality Measures</h3>
                <div className="space-y-2">
                  {domains.map(d => {
                    const data = hhcaps[d.key];
                    if (!data) return null;
                    const score = data.score;
                    return (
                      <div key={d.key} className="flex items-start justify-between py-2 border-b border-gray-100 last:border-0">
                        <span className="text-sm text-gray-700">{d.label}</span>
                        <div className="text-right">
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                            score === 'meets' ? 'bg-green-100 text-green-700' :
                            score === 'partial' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {score === 'meets' ? 'Meets' : score === 'partial' ? 'Partial' : 'Not Met'}
                          </span>
                          {data.notes && <p className="text-xs text-gray-400 mt-1 max-w-xs text-right">{data.notes}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Score & Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="glass-card rounded-2xl shadow-sm border border-teal-100/50 p-6 text-center">
              <p className="text-sm text-gray-500">Compliance Score</p>
              <p className={`text-5xl font-bold mt-1 ${scoreColor}`}>{audit.compliance_score}</p>
              <p className="text-gray-400 text-xs mt-1">out of 100</p>
            </div>
            <div className="glass-card rounded-2xl shadow-sm border border-teal-100/50 p-6 text-center">
              <p className="text-sm text-gray-500">Critical</p>
              <p className="text-3xl font-bold text-red-600 mt-1">{audit.critical_count}</p>
            </div>
            <div className="glass-card rounded-2xl shadow-sm border border-teal-100/50 p-6 text-center">
              <p className="text-sm text-gray-500">Warnings</p>
              <p className="text-3xl font-bold text-yellow-600 mt-1">{audit.warning_count}</p>
            </div>
            <div className="glass-card rounded-2xl shadow-sm border border-teal-100/50 p-6 text-center">
              <p className="text-sm text-gray-500">Informational</p>
              <p className="text-3xl font-bold text-teal-600 mt-1">{audit.info_count}</p>
            </div>
          </div>

          {/* ========== MISSING DOCUMENTS CHECKLIST ========== */}
          {hasCriticalOrWarning && (() => {
            const isHospice = audit.service_type === 'hospice';
            const missingKeywords = ['missing', 'not found', 'not present', 'not included', 'not signed', 'unsigned', 'blank', 'absent', 'no ', 'not documented', 'not provided', 'incomplete', 'lacks'];
            const issueFindings = findings.filter(f => f.severity === 'critical' || f.severity === 'warning');
            const missingDocs = issueFindings.map(f => {
              const desc = (f.description || '').toLowerCase() + ' ' + (f.title || '').toLowerCase();
              const isMissing = missingKeywords.some(kw => desc.includes(kw));
              let suggestedDoc = '';
              const titleLower = (f.title || '').toLowerCase();
              const catLower = (f.category || '').toLowerCase();

              if (titleLower.includes('signature') || titleLower.includes('signed') || titleLower.includes('unsigned')) {
                suggestedDoc = 'Signed copy of ' + (f.note_type || f.category || 'the referenced document');
              } else if (catLower.includes('election') || titleLower.includes('election')) {
                suggestedDoc = 'Completed Hospice Election Statement';
              } else if (catLower.includes('cti') || catLower.includes('certification') || titleLower.includes('terminal illness')) {
                suggestedDoc = 'Certification of Terminal Illness (CTI)';
              } else if (catLower.includes('idg') || titleLower.includes('interdisciplinary')) {
                suggestedDoc = 'IDG Meeting Notes';
              } else if (catLower.includes('bereavement') || titleLower.includes('bereavement')) {
                suggestedDoc = 'Bereavement Plan documentation';
              } else if (catLower.includes('volunteer') || titleLower.includes('volunteer')) {
                suggestedDoc = 'Volunteer Services documentation';
              } else if (catLower.includes('aide') || titleLower.includes('aide')) {
                suggestedDoc = isHospice ? 'Hospice Aide Supervision documentation' : 'Home Health Aide documentation';
              } else if (catLower.includes('gip') || titleLower.includes('general inpatient')) {
                suggestedDoc = 'GIP (General Inpatient Care) Records';
              } else if (catLower.includes('continuous') || titleLower.includes('continuous care')) {
                suggestedDoc = 'Continuous Home Care Records';
              } else if (catLower.includes('face') || titleLower.includes('f2f') || titleLower.includes('face-to-face')) {
                suggestedDoc = 'Face-to-Face Encounter documentation';
              } else if (catLower.includes('oasis') || titleLower.includes('oasis')) {
                suggestedDoc = 'Corrected OASIS assessment';
              } else if (catLower.includes('plan') || catLower.includes('485') || titleLower.includes('plan of care')) {
                suggestedDoc = isHospice ? 'Updated Hospice Plan of Care' : 'Updated Plan of Care / 485';
              } else if (catLower.includes('order') || titleLower.includes('physician order')) {
                suggestedDoc = 'Signed Physician Orders';
              } else if (isMissing) {
                suggestedDoc = f.note_type || f.category || 'Supporting documentation';
              }
              return { ...f, isMissing, suggestedDoc };
            }).filter(f => f.isMissing && f.suggestedDoc);

            if (missingDocs.length === 0) return null;

            const seen = new Set();
            const uniqueMissing = missingDocs.filter(d => {
              if (seen.has(d.suggestedDoc)) return false;
              seen.add(d.suggestedDoc);
              return true;
            });

            return (
              <div className="mb-6 glass-card rounded-2xl shadow-sm border border-red-200/70 p-5">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-bold text-gray-800">Documents Needed to Resolve Issues</p>
                    <p className="text-sm text-gray-500">
                      Issues found in <span className="font-semibold text-gray-700">{audit.original_filename}</span> — upload these to improve your compliance score
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  {uniqueMissing.map((doc, idx) => (
                    <div key={idx} className="flex items-start space-x-3 bg-red-50/50 rounded-xl px-4 py-3">
                      <div className="w-5 h-5 border-2 border-red-300 rounded flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-gray-800">{doc.suggestedDoc}</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          <span className={`font-semibold ${doc.severity === 'critical' ? 'text-red-600' : 'text-yellow-600'}`}>
                            {doc.severity === 'critical' ? 'CRITICAL' : 'WARNING'}:
                          </span>{' '}
                          {doc.title}
                        </p>
                        {doc.page_numbers && (
                          <p className="text-xs text-gray-400 mt-0.5">Found in: {audit.original_filename} — {doc.page_numbers}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-3 text-center">
                  Upload these documents below to trigger a re-audit with updated findings
                </p>
              </div>
            );
          })()}

          {/* ========== SUPPLEMENTAL DOCUMENTS SECTION ========== */}
          {hasCriticalOrWarning && (
            <div className="mb-6">
              <div className="glass-card rounded-2xl shadow-sm border border-orange-200/70 overflow-hidden">
                <button
                  onClick={() => setShowSupplemental(!showSupplemental)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-orange-50/50 transition"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                      <svg className="w-5 h-5 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800">Upload Supporting Documents to Re-Audit</p>
                      <p className="text-sm text-gray-500">
                        Have missing documents that address the findings above? Upload them and the AI will re-analyze everything together.
                      </p>
                    </div>
                  </div>
                  <span className="text-gray-400 text-lg flex-shrink-0 ml-4">{showSupplemental ? '\u2212' : '+'}</span>
                </button>

                {showSupplemental && (
                  <div className="px-6 pb-6 border-t border-orange-100">
                    {/* Previously uploaded supplemental docs */}
                    {suppDocs.length > 0 && (
                      <div className="mt-4 mb-4">
                        <p className="text-xs font-semibold text-gray-500 mb-2">Previously uploaded supplemental documents:</p>
                        <div className="space-y-2">
                          {suppDocs.map(doc => (
                            <div key={doc.id} className="flex items-center justify-between bg-green-50 border border-green-200 rounded-xl px-3 py-2">
                              <div className="flex items-center space-x-2">
                                <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                                <span className="text-sm text-gray-700">{doc.original_filename}</span>
                              </div>
                              <span className="text-xs text-gray-400">{new Date(doc.created_at).toLocaleDateString()}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {suppError && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg mb-3 text-sm">{suppError}</div>
                    )}

                    {/* Dropzone */}
                    <div {...getSuppRootProps()}
                      className={`mt-4 border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition
                        ${isSuppDragActive ? 'border-orange-500 bg-orange-50' : 'border-gray-300 hover:border-orange-400 hover:bg-orange-50/30'}`}>
                      <input {...getSuppInputProps()} />
                      <svg className="w-8 h-8 mx-auto text-orange-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <p className="font-medium text-gray-700 text-sm">
                        Drop supporting documents here, or click to select
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        e.g. signed physician orders, missing F2F encounter, corrected OASIS
                      </p>
                    </div>

                    {/* File list */}
                    {suppFiles.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {suppFiles.map((file, idx) => (
                          <div key={idx} className="flex items-center justify-between bg-white border border-gray-200 rounded-xl px-3 py-2">
                            <div className="flex items-center space-x-2">
                              <svg className="w-4 h-4 text-teal-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              <span className="text-sm text-gray-700">{file.name}</span>
                              <span className="text-xs text-gray-400">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                            </div>
                            <button onClick={() => removeSuppFile(idx)} className="text-red-400 hover:text-red-600 text-xs font-medium">Remove</button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Submit button */}
                    <button
                      onClick={handleSupplementalUpload}
                      disabled={suppFiles.length === 0 || suppUploading}
                      className="mt-4 w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold py-2.5 rounded-xl shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition"
                    >
                      {suppUploading
                        ? 'Uploading & Starting Re-Audit...'
                        : `Upload ${suppFiles.length} Document${suppFiles.length !== 1 ? 's' : ''} & Re-Audit`}
                    </button>
                    <p className="text-xs text-gray-400 text-center mt-2">
                      The AI will re-analyze the original document together with all supplemental documents to recalculate the compliance score.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Filter Tabs */}
          <div className="flex space-x-2 mb-4">
            {['all', 'critical', 'warning', 'informational'].map(sev => (
              <button key={sev} onClick={() => setFilterSeverity(sev)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${
                  filterSeverity === sev ? 'bg-teal-700 text-white shadow-md' : 'bg-white/80 text-gray-600 hover:bg-teal-50 border border-gray-200'
                }`}>
                {sev === 'all' ? `All (${findings.length})` :
                  `${sev.charAt(0).toUpperCase() + sev.slice(1)} (${findings.filter(f => f.severity === sev).length})`}
              </button>
            ))}
          </div>

          {/* Findings List */}
          <div className="space-y-3">
            {filteredFindings.length === 0 ? (
              <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center">
                <p className="text-green-800 font-semibold">No findings in this category</p>
              </div>
            ) : (
              filteredFindings.map((finding) => {
                const config = severityConfig[finding.severity];
                const isExpanded = expandedFindings[finding.id];
                return (
                  <div key={finding.id}
                    className={`${config.bg} border ${config.border} rounded-2xl overflow-hidden`}>
                    <button onClick={() => toggleFinding(finding.id)}
                      className="w-full flex items-center justify-between p-4 text-left">
                      <div className="flex items-center space-x-3">
                        <span className={`${config.iconBg} text-white w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold`}>
                          {config.icon}
                        </span>
                        <div>
                          <span className={`${config.badge} text-xs font-semibold px-2 py-0.5 rounded-full`}>
                            {finding.severity.toUpperCase()}
                          </span>
                          <span className="text-sm text-gray-500 ml-2">{finding.category}</span>
                          <p className="font-semibold text-gray-800 mt-1">{finding.title}</p>
                        </div>
                      </div>
                      <span className="text-gray-400 text-lg">{isExpanded ? '\u2212' : '+'}</span>
                    </button>
                    {isExpanded && (
                      <div className="px-4 pb-4 pl-14 space-y-3">
                        {(finding.page_numbers || finding.note_type || finding.oasis_field) && (
                          <div className="flex flex-wrap gap-2">
                            {finding.page_numbers && (
                              <span className="bg-gray-200 text-gray-700 text-xs font-semibold px-2 py-1 rounded">
                                {finding.page_numbers}
                              </span>
                            )}
                            {finding.note_type && (
                              <span className="bg-gray-100 text-gray-600 text-xs font-medium px-2 py-1 rounded">
                                {finding.note_type}
                              </span>
                            )}
                            {finding.oasis_field && (
                              <span className="bg-purple-100 text-purple-700 text-xs font-medium px-2 py-1 rounded">
                                OASIS: {finding.oasis_field}
                              </span>
                            )}
                          </div>
                        )}

                        <p className="text-gray-700 text-sm">{finding.description}</p>

                        {finding.direct_quote && (
                          <div className="border-l-4 border-teal-300 bg-white bg-opacity-70 pl-3 py-2 rounded-r-lg">
                            <p className="text-xs text-gray-500 font-medium mb-1">From Document:</p>
                            <p className="text-sm text-gray-600 italic">"{finding.direct_quote}"</p>
                          </div>
                        )}

                        {finding.recommendation && (
                          <div className="bg-white bg-opacity-60 rounded-xl p-3">
                            <p className="text-sm font-semibold text-green-800">Recommendation:</p>
                            <p className="text-sm text-gray-700 mt-1">{finding.recommendation}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Email Status */}
          {audit.email_sent && (
            <div className="mt-6 bg-green-50 border border-green-200 rounded-xl p-4 text-sm text-green-700">
              PDF report was emailed to your agency on {new Date(audit.email_sent_at).toLocaleString()}
            </div>
          )}
        </>
      )}
    </div>
  );
}
